import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BellRing,
  Building2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Leaf,
  MapPin,
  Users
} from 'lucide-react';

import Header from '../components/Header';
import Footer from '../components/Footer';
import ImageMarquee from '../components/ImageMarquee';
import api from '../services/api';

const LeaderCard = ({ leader }) => (
  <div className="group h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
    <div className="mx-auto h-56 w-full overflow-hidden rounded-xl bg-gradient-to-br from-slate-50 to-blue-50">
      <img
        src={leader.image}
        alt={leader.name}
        className="h-full w-full object-contain p-2 transition-transform duration-500 group-hover:scale-[1.03]"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
    </div>

    <div className="pt-5 text-center">
      <h3 className="text-lg font-bold text-slate-900">{leader.name}</h3>
      <p className="mt-2 text-sm font-medium leading-6 text-blue-700">{leader.designation}</p>
    </div>
  </div>
);

const LandingPage = () => {
  const navigate = useNavigate();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [natureClubs, setNatureClubs] = useState([]);
  const [natureLoading, setNatureLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState(new Set());

 const latestTender = {
  name: 'Social Audit Society of Tamil Nadu (SASTA) – Selection of a candidate for the post of Director',
  date: '2026-10-08',
  file: 'tenders/TENDER_Social_Audit_Society_of_Tamil_Nadu_SASTA_1791478002719.pdf'
};

  const carouselImages = useMemo(
    () => [
      '/images/slide1.jpeg',
      '/images/slide2.jpeg',
      '/images/slide3.jpeg',
      '/images/slide4.jpeg',
      '/images/slide5.jpeg',
      '/images/slide6.jpeg',
      '/images/slide7.jpeg',
      '/images/about5.png.jpg',
      '/images/about6.png.jpg',
      '/images/mark2.jpg',
      '/images/mark3.jpg',
      '/images/mark4.jpg',
      '/images/mark1.jpg'
    ],
    []
  );

  const instituteImagePath = '/images/ins.png';

  const leadership = [
    {
      image: '/images/cmtn.png',
      name: 'Thiru C. Joseph Vijay',
      designation: "Hon'ble Chief Minister of Tamil Nadu"
    },
    {
      image: '/images/leader2.jpeg',
      name: 'Thiru N. Anand',
      designation: "Hon'ble Minister for Rural Development and Panchayat Raj"
    },
    {
  image: "/images/leader4.png",
  name: "Thiru Dharmendra Pratap Yadav, IAS.,",
  designation: "Principal Secretary To Government.",
},
    {
      image: '/images/leader3.jpeg',
      name: 'Dr. R. Anandakumar, I.A.S.',
      designation: 'Commissioner of Rural Development and Panchayat Raj, Govt. of Tamil Nadu'
    }
  ];

  useEffect(() => {
    const fetchNatureClubs = async () => {
      try {
        const response = await fetch(`${api.BASE_URL}/admin/nature`);
        const result = await response.json();

        if (result?.data && Array.isArray(result.data)) {
          setNatureClubs(result.data);
        }
      } catch (error) {
        console.error('Error fetching nature clubs:', error);
      } finally {
        setNatureLoading(false);
      }
    };

    fetchNatureClubs();
  }, []);



  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === carouselImages.length - 1 ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const previousSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? carouselImages.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === carouselImages.length - 1 ? 0 : prev + 1));
  };

  const handleNatureClubClick = (code) => {
    navigate(`/nature-club/${code}`);
  };

  const handleImageError = (clubId) => {
    setImageErrors((prev) => new Set([...prev, clubId]));
  };

  const getTenderUrl = (filePath) => {
    if (!filePath) return '';

    const cleanPath = String(filePath)
      .trim()
      .replace(/\\/g, '/')
      .replace(/^\/+/, '');

    if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
      return cleanPath;
    }

    if (cleanPath.startsWith('uploads/')) {
      return `https://crd-training.in/${cleanPath}`;
    }

    return `https://crd-training.in/uploads/${cleanPath}`;
  };

  const formatTenderDate = (value) => {
    if (!value) return '-';

    const datePart = String(value).slice(0, 10);
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
  };

  const viewTender = () => {
  if (!latestTender?.file) return;

  const url = getTenderUrl(latestTender.file);

  window.open(url, '_blank', 'noopener,noreferrer');
};

 const downloadTender = async () => {
  if (!latestTender?.file) return;

  const url = getTenderUrl(latestTender.file);

  try {
    const response = await fetch(url, {
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('PDF download failed');
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = 'Social_Audit_Society_of_Tamil_Nadu_SASTA.pdf';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Tender download error:', error);
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Header />

      <main className="flex-grow">

  {/* =====================================================
      HERO CAROUSEL
      ===================================================== */}
  <section className="relative h-[calc(100vh-120px)] min-h-[560px] md:h-[calc(100vh-100px)] lg:h-[calc(100vh-100px)] overflow-hidden">

    {/* Carousel Images */}
    {carouselImages.map((image, index) => (
      <div
        key={image}
        className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${
          index === currentSlide
            ? 'opacity-100'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        <img
          src={image}
          alt={`Background ${index + 1}`}
          className="w-full h-full object-cover"
        />

        {/* OLD LANDING PAGE OVERLAY - KEEP THIS */}
        <div className="absolute inset-0 bg-black/45"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-black/70"></div>
      </div>
    ))}


 {/* =====================================================
    HERO CONTENT
    ===================================================== */}
<div className="relative z-10 h-full">

  {/* LEFT SIDE CONTENT */}
  <div className="container mx-auto h-full px-6 flex items-center pt-60 lg:pt-0">
    <div className="max-w-3xl">

      <h1 className="text-3xl sm:text-3xl lg:text-[30px] xl:text-[30px] leading-tight font-bold text-white shadow-text">
        COMMISSIONERATE OF RURAL DEVELOPMENT AND PANCHAYAT RAJ

        <span className="block mt-1 text-blue-200">
          (TRAINING)
        </span>
      </h1>

      <p className="mt-4 max-w-2xl text-sm sm:text-base leading-relaxed text-white shadow-text">
        Rural Development and Panchayat Raj Department, Govt. of Tamil Nadu
      </p>

      <div className="mt-7 flex flex-wrap gap-3">

        <button
          type="button"
          onClick={() => navigate('/about')}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-5 py-3 text-sm sm:text-base font-semibold text-white shadow-md transition-all duration-200 hover:bg-blue-800 hover:-translate-y-0.5"
        >
          About the Commissionerate
          <ArrowRight size={18} />
        </button>

        <button
          type="button"
          onClick={() => navigate('/documents')}
          className="inline-flex items-center gap-2 rounded-lg border border-white/50 bg-black/30 px-5 py-3 text-sm sm:text-base font-semibold text-white backdrop-blur-sm transition-all duration-200 hover:bg-black/45 hover:-translate-y-0.5"
        >
          <FileText size={18} />
          Documents & Downloads
        </button>

      </div>

    </div>
  </div>

  {/* =====================================================
      TENDER NOTIFICATION - TOP RIGHT
      ===================================================== */}
  <div className="absolute top-4 right-4 z-30 w-[calc(100%-2rem)] max-w-md lg:top-8 lg:right-10">

    <div className="overflow-hidden rounded-2xl border-2 border-amber-400 bg-white shadow-2xl shadow-black/60">

      {/* HEADER */}
      <div className="border-b border-amber-400 bg-gradient-to-r from-amber-300 via-yellow-200 to-orange-200 px-5 py-4">

        <div className="flex items-center gap-3">

          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-amber-600 text-white shadow">
            <BellRing size={22} />
          </div>

          <div>
            <div className="text-sm font-extrabold uppercase tracking-wider text-red-800">
              Latest Notification
            </div>

            <div className="mt-1 text-xs font-semibold text-gray-700">
              Recruitments / Tender Document
            </div>
          </div>

        </div>

      </div>

      {/* BODY */}
      <div className="bg-white p-5">

        <h2 className="text-base sm:text-lg font-bold leading-6 text-gray-950">
          {latestTender.name}
        </h2>

        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-100 px-3 py-3 text-sm text-blue-950">

          <div className="flex items-center gap-2 font-semibold">
            <CalendarDays size={16} />
            Published Date
          </div>

          <div className="mt-1 font-bold">
            {formatTenderDate(latestTender.date)}
          </div>

        </div>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row">

          <button
            type="button"
            onClick={viewTender}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-800 px-4 py-3 text-sm font-semibold text-white shadow transition hover:bg-blue-900"
          >
            <Eye size={17} />
            View PDF
          </button>

          <button
            type="button"
            onClick={downloadTender}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-400 bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-900 transition hover:bg-gray-200"
          >
            <Download size={17} />
            Download PDF
          </button>

        </div>

      </div>

    </div>

  </div>

</div>

    {/* =====================================================
        PREVIOUS BUTTON
        ===================================================== */}
    <button
      type="button"
      onClick={previousSlide}
      className="absolute left-4 top-1/2 z-20 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center bg-white/20 text-white hover:bg-white/30 transition-colors backdrop-blur-sm"
      aria-label="Previous slide"
    >
      <ChevronLeft size={24} />
    </button>


    {/* =====================================================
        NEXT BUTTON
        ===================================================== */}
    <button
      type="button"
      onClick={nextSlide}
      className="absolute right-4 top-1/2 z-20 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center bg-white/20 text-white hover:bg-white/30 transition-colors backdrop-blur-sm"
      aria-label="Next slide"
    >
      <ChevronRight size={24} />
    </button>


    {/* =====================================================
        CAROUSEL DOTS
        ===================================================== */}
    <div className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2 flex space-x-2">

      {carouselImages.map((_, index) => (

        <button
          key={index}
          type="button"
          onClick={() => goToSlide(index)}
          className={`h-2 rounded-full transition-all ${
            index === currentSlide
              ? 'bg-white w-6'
              : 'bg-white/50 w-2'
          }`}
          aria-label={`Go to slide ${index + 1}`}
        />

      ))}

    </div>

  </section>
        

        <section className="bg-white py-16 sm:py-20">
          <div className="container mx-auto px-6 lg:px-8">
            <div className="mx-auto max-w-7xl">
              <div className="mx-auto mb-12 max-w-3xl text-center">
                <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-widest text-blue-700">
                  Leadership
                </span>
                <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
                  Guiding our mission forward
                </h2>
                
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
                {leadership.map((leader) => (
                  <LeaderCard key={leader.name} leader={leader} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50 via-white to-white py-16 sm:py-20">
          <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-emerald-100/60 blur-3xl" />
          <div className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-cyan-100/50 blur-3xl" />

          <div className="relative container mx-auto px-6 lg:px-8">
            <div className="mx-auto max-w-7xl">
              <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-widest text-emerald-700">
                    <Leaf size={14} />
                    SIRD Eco Park
                  </span>

                  <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
                    Discover our flora and fauna
                  </h2>

                  <p className="mt-3 max-w-2xl text-slate-600">
                    Explore the biodiversity and learning environment within the SIRD&PR campus.
                  </p>
                </div>
              </div>

              {natureLoading ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="h-80 animate-pulse rounded-2xl bg-white shadow-sm" />
                  ))}
                </div>
              ) : natureClubs.length > 0 ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {natureClubs.map((club) => (
                    <button
                      type="button"
                      key={club.id}
                      onClick={() => handleNatureClubClick(club.code)}
                      className="group overflow-hidden rounded-2xl border border-emerald-100 bg-white text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                    >
                      <div className="relative h-52 overflow-hidden bg-gradient-to-br from-emerald-100 to-cyan-100">
                        {club.image && !imageErrors.has(club.id) ? (
                          <img
                            src={`https://crd-training.in/uploads/nature/${club.image}`}
                            alt={club.name}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                            onError={() => handleImageError(club.id)}
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center">
                            <Leaf className="text-emerald-600" size={64} strokeWidth={1.4} />
                          </div>
                        )}

                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent opacity-80" />
                      </div>

                      <div className="p-5">
                        <h3 className="line-clamp-1 text-lg font-bold text-slate-900 transition-colors group-hover:text-emerald-700">
                          {club.name}
                        </h3>

                        {club.name_ta && (
                          <p className="mt-1 line-clamp-1 text-sm font-medium text-emerald-700">{club.name_ta}</p>
                        )}

                        {club.common && (
                          <p className="mt-3 line-clamp-2 text-sm leading-6 text-slate-500">{club.common}</p>
                        )}

                        <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
                          Learn More
                          <ArrowRight className="transition-transform group-hover:translate-x-1" size={16} />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-emerald-200 bg-white p-10 text-center text-slate-500">
                  Eco Park information is currently unavailable.
                </div>
              )}
            </div>
          </div>
        </section>

        <section id="overview" className="bg-white py-16 sm:py-20">
          <div className="container mx-auto px-6 lg:px-8">
            <div className="mx-auto max-w-7xl">
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
                <div className="lg:sticky lg:top-24">
                  <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-widest text-blue-700">
                    About CRD Training
                  </span>

                  <h2 className="mt-4 text-3xl font-black leading-tight text-slate-900 sm:text-4xl">
                    Commissionerate of Rural Development & Panchayat Raj (Training)
                  </h2>

                  <p className="mt-5 text-base leading-7 text-slate-600">
                    Rural Development and Panchayat Raj Department, Government of Tamil Nadu.
                  </p>

                  <button
                    type="button"
                    onClick={() => navigate('/about')}
                    className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
                  >
                    Read More
                    <ArrowRight size={17} />
                  </button>
                </div>

                <div className="space-y-5">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-7">
                    <div className="flex gap-4">
                      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                        <Building2 size={22} />
                      </div>

                      <div>
                        <h3 className="text-xl font-bold text-slate-900">Commissionerate of RD&PR (Training)</h3>
                        <p className="mt-3 leading-7 text-slate-600">
                          The Commissionerate of RD&PR (Training) undertakes planning, implementation and monitoring of capacity building and training activities in the state through SIRD&PR and five RIRD&PR institutes.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-7">
                    <div className="flex gap-4">
                      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                        <Users size={22} />
                      </div>

                      <div>
                        <h3 className="text-xl font-bold text-slate-900">Mission & Capacity Building</h3>
                        <p className="mt-3 leading-7 text-slate-600">
                          SIRD&PR conceptualizes, designs, organizes, coordinates and monitors training and capacity building for elected representatives of three-tier Panchayat Raj Institutions, RD&PR officials, line departments, CBOs and the public.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-7">
                    <div className="flex gap-4">
                      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                        <MapPin size={22} />
                      </div>

                      <div>
                        <h3 className="text-xl font-bold text-slate-900">Regional Institutes Network</h3>
                        <p className="mt-3 leading-7 text-slate-600">
                          Regional Institutes at Sathiya Vijaya Nagaram, Krishnagiri, Pattukottai, Bhavanisagar and T.Kallupatty support training delivery across Tamil Nadu.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-slate-50 py-16 sm:py-20">
          <div className="container mx-auto px-6 lg:px-8">
            <div className="mx-auto max-w-7xl">
              <div className="mb-10 text-center">
                <span className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-widest text-slate-600 shadow-sm">
                  Our Institutes
                </span>

                <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
                  Training network across Tamil Nadu
                </h2>

                <p className="mx-auto mt-3 max-w-2xl text-slate-600">
                  A connected institutional network supporting capacity building and rural governance.
                </p>
              </div>

              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-3 shadow-lg shadow-slate-200/60">
                <img
                  src={instituteImagePath}
                  alt="Our Institutes Network"
                  className="h-auto w-full rounded-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white py-12">
          <ImageMarquee />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LandingPage;
